"use client";

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/OS/integrations/supabase/client';
import { useToast } from '@/OS/components/ui/use-toast';
import { APP_VERSION } from '@/utils/version';
import { Input } from '@/OS/components/ui/input';
import { Label } from '@/OS/components/ui/label';
import { Textarea } from '@/OS/components/ui/textarea';
import { Button } from '@/OS/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UploadCloud, Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

type MapleOSEdition = 'MapleOS' | 'MapleOS Pro' | 'MapleOS Enterprise';
type RuntimeEnvironment = 'browser' | 'native_desktop' | 'bare_metal';
type NativeOperatingSystem =
  | 'MapleOS Bare Metal'
  | 'Microsoft Windows 10'
  | 'Microsoft Windows 11'
  | 'Microsoft Windows Not 10/11'
  | 'Debian Based Distro'
  | 'RPM Based Distro'
  | 'Apple OSX'
  | 'Other';
type BrowserOption = 'Google Chrome' | 'Microsoft Edge' | 'Firefox' | 'Opera' | 'Brave' | 'Other';

const MAPLEOS_EDITIONS: MapleOSEdition[] = ['MapleOS', 'MapleOS Pro', 'MapleOS Enterprise'];
const NATIVE_OS_OPTIONS: NativeOperatingSystem[] = [
  'MapleOS Bare Metal',
  'Microsoft Windows 10',
  'Microsoft Windows 11',
  'Microsoft Windows Not 10/11',
  'Debian Based Distro',
  'RPM Based Distro',
  'Apple OSX',
  'Other',
];
const BROWSER_OPTIONS: BrowserOption[] = [
  'Google Chrome',
  'Microsoft Edge',
  'Firefox',
  'Opera',
  'Brave',
  'Other',
];

const detectNativeOperatingSystem = (): NativeOperatingSystem => {
  const nav = navigator as Navigator & {
    userAgentData?: {
      platform?: string;
      platformVersion?: string;
    };
  };
  const userAgent = navigator.userAgent;
  const platform = nav.userAgentData?.platform || navigator.platform || userAgent;
  const platformVersion = Number.parseInt(nav.userAgentData?.platformVersion?.split('.')[0] ?? '', 10);

  if (/Windows/i.test(platform) || /Windows/i.test(userAgent)) {
    if (!Number.isNaN(platformVersion)) {
      return platformVersion >= 13 ? 'Microsoft Windows 11' : 'Microsoft Windows 10';
    }

    if (/Windows NT 10\.0/i.test(userAgent)) {
      return 'Microsoft Windows 10';
    }

    return 'Microsoft Windows Not 10/11';
  }

  if (/Ubuntu|Debian|Mint|Pop!_OS|Pop_OS|Kubuntu|Xubuntu|Lubuntu|Zorin|Elementary|MX Linux|Kali/i.test(userAgent)) {
    return 'Debian Based Distro';
  }

  if (/Fedora|Red Hat|RHEL|CentOS|Rocky|AlmaLinux|SUSE|openSUSE/i.test(userAgent)) {
    return 'RPM Based Distro';
  }

  if (/Macintosh|Mac OS X|MacIntel|MacPPC|Mac68K/i.test(platform) || /Macintosh|Mac OS X/i.test(userAgent)) {
    return 'Apple OSX';
  }

  if (/Linux/i.test(platform) || /Linux/i.test(userAgent)) {
    return 'Other';
  }

  return 'Other';
};

const detectBrowser = (): BrowserOption => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Edg/')) return 'Microsoft Edge';
  if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) return 'Opera';
  if (userAgent.includes('Brave/')) return 'Brave';
  if (userAgent.includes('Firefox/')) return 'Firefox';
  if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) return 'Google Chrome';

  return 'Other';
};

const getRuntimeOptions = (edition: MapleOSEdition) => {
  const base = [
    { value: 'browser' as const, label: 'Browser' },
    { value: 'native_desktop' as const, label: 'Native desktop application' },
  ];

  if (edition === 'MapleOS') {
    return base;
  }

  return [...base, { value: 'bare_metal' as const, label: 'Bare metal install' }];
};

const BugReport: React.FC = () => {
  const currentVersion = 'v' + APP_VERSION;
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mapleosEdition, setMapleosEdition] = useState<MapleOSEdition>('MapleOS');
  const [runtimeEnvironment, setRuntimeEnvironment] = useState<RuntimeEnvironment>('browser');
  const [nativeOperatingSystem, setNativeOperatingSystem] = useState<NativeOperatingSystem | ''>('');
  const [browserDetails, setBrowserDetails] = useState<BrowserOption>('Other');
  const [timeOccurred, setTimeOccurred] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runtimeOptions = useMemo(() => getRuntimeOptions(mapleosEdition), [mapleosEdition]);
  const needsBrowserDetails = runtimeEnvironment === 'browser';

  useEffect(() => {
    setMapleosEdition('MapleOS');
    setRuntimeEnvironment('browser');
    setNativeOperatingSystem(detectNativeOperatingSystem());
    setBrowserDetails(detectBrowser());
    setTimeOccurred(new Date().toISOString().slice(0, 16));
    setDescription('');
    setFile(null);
    setIsDraggingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    if (mapleosEdition === 'MapleOS' && runtimeEnvironment === 'bare_metal') {
      setRuntimeEnvironment('browser');
    }
  }, [mapleosEdition, runtimeEnvironment]);

  useEffect(() => {
    if (runtimeEnvironment === 'bare_metal') {
      setNativeOperatingSystem('MapleOS Bare Metal');
      setBrowserDetails('Other');
      return;
    }

    if (nativeOperatingSystem === 'MapleOS Bare Metal') {
      setNativeOperatingSystem(detectNativeOperatingSystem());
    }

    if (runtimeEnvironment !== 'browser') {
      setBrowserDetails('Other');
    }
  }, [runtimeEnvironment, nativeOperatingSystem]);

  const handleSubmit = async () => {
    if (!nativeOperatingSystem || !timeOccurred || !description.trim()) {
      toast({
        title: 'Missing info',
        description: 'Please fill out all required fields.',
        variant: 'destructive',
      });
      return;
    }

    if (needsBrowserDetails && !browserDetails.trim()) {
      toast({
        title: 'Browser details required',
        description: 'Please tell us which browser you are using.',
        variant: 'destructive',
      });
      return;
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please log in before submitting a bug report.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let uploadedFileURL: string | undefined;
      let mediaType: string | undefined;

      if (file) {
        const fileExtension = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExtension}`;
        const filePath = `bug-reports/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('bug-media')
          .upload(filePath, file, { cacheControl: '3600', upsert: false });

        if (uploadError) {
          throw new Error(`Failed to upload file: ${uploadError.message}`);
        }

        const { data: publicUrlData } = supabase.storage
          .from('bug-media')
          .getPublicUrl(filePath);

        uploadedFileURL = publicUrlData.publicUrl;
        mediaType = 'file';
      }

      const bugReports = supabase.from('bug_reports') as any;
      const { error: insertError } = await bugReports.insert({
        mapleos_edition: mapleosEdition,
        runtime_environment: runtimeEnvironment,
        os: nativeOperatingSystem,
        browser: needsBrowserDetails ? browserDetails.trim() : null,
        time_occurred: new Date(timeOccurred).toISOString(),
        version: currentVersion,
        media_type: mediaType,
        screenshot_video_url: uploadedFileURL,
        description: description.trim(),
        user_id: user.id,
        user_agent: navigator.userAgent,
      });

      if (insertError) {
        throw new Error(`Failed to insert bug report: ${insertError.message}`);
      }

      toast({
        title: 'Thank you!',
        description: 'Your bug report has been submitted.',
      });

      window.dispatchEvent(new CustomEvent('mapleos-close-active-window', {}));
    } catch (err: any) {
      toast({
        title: 'Error',
        description: `Failed to submit bug report: ${err?.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelection = (nextFile: File | null) => {
    if (!nextFile) {
      setFile(null);
      return;
    }

    if (!nextFile.type.startsWith('image/') && !nextFile.type.startsWith('video/')) {
      toast({
        title: 'Unsupported file type',
        description: 'Please use an image or video file.',
        variant: 'destructive',
      });
      return;
    }

    setFile(nextFile);
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    handleFileSelection(event.dataTransfer.files?.[0] || null);
  };

  return (
    <div className="h-full w-full overflow-auto p-4">
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="mapleosEdition">MapleOS edition</Label>
            <Select value={mapleosEdition} onValueChange={(value) => setMapleosEdition(value as MapleOSEdition)}>
              <SelectTrigger id="mapleosEdition">
                <SelectValue placeholder="Select MapleOS edition" />
              </SelectTrigger>
              <SelectContent>
                {MAPLEOS_EDITIONS.map((edition) => (
                  <SelectItem key={edition} value={edition}>
                    {edition}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="runtimeEnvironment">Where are you running it?</Label>
            <Select
              value={runtimeEnvironment}
              onValueChange={(value) => setRuntimeEnvironment(value as RuntimeEnvironment)}
            >
              <SelectTrigger id="runtimeEnvironment">
                <SelectValue placeholder="Select runtime" />
              </SelectTrigger>
              <SelectContent>
                {runtimeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="nativeOperatingSystem">Native Operating System</Label>
          <Select
            value={nativeOperatingSystem}
            onValueChange={(value) => setNativeOperatingSystem(value as NativeOperatingSystem)}
          >
            <SelectTrigger id="nativeOperatingSystem">
              <SelectValue placeholder="Select native operating system" />
            </SelectTrigger>
            <SelectContent>
              {NATIVE_OS_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {needsBrowserDetails ? (
          <div className="space-y-2">
            <Label htmlFor="browserDetails">Browser details</Label>
            <Select
              value={browserDetails}
              onValueChange={(value) => setBrowserDetails(value as BrowserOption)}
            >
              <SelectTrigger id="browserDetails">
                <SelectValue placeholder="Select browser" />
              </SelectTrigger>
              <SelectContent>
                {BROWSER_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="timeOccurred">When did it happen?</Label>
          <Input
            type="datetime-local"
            id="timeOccurred"
            value={timeOccurred}
            onChange={(e) => setTimeOccurred(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">What went wrong?</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder="Describe what you were doing when the bug occurred..."
          />
        </div>

        <div className="space-y-2">
          <Label>Attach Screenshot or Video (optional)</Label>
          <div
            className={`rounded-xl border border-dashed p-4 transition-colors ${
              isDraggingFile ? 'border-primary bg-primary/10' : 'border-border/60 bg-muted/20'
            }`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDraggingFile(true);
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleFileDrop}
          >
            <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
              <UploadCloud className="h-4 w-4" />
              Drag an image or video here, or choose a file below.
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept="image/*,video/*"
                ref={fileInputRef}
                onChange={(e) => handleFileSelection(e.target.files?.[0] || null)}
              />
              {file && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setIsDraggingFile(false);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
          {file && (
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <UploadCloud className="h-4 w-4" />
              {file.name}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => window.dispatchEvent(new CustomEvent('mapleos-close-active-window', {}))}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export { BugReport };